import React, { useEffect, useState, useCallback, MouseEvent } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ScrollToTopOnMount from "../../../../ScrollToTop";
import TopicAddForm from "./TopicAddForm";
import NoTopicMessage from "./NoTopicMessage";
import Loading from "../../../Loading";
import PageTitle from "components/common/Layout/PageTitle";
import BackButton from "components/common/BackButton";
import Pagination from "components/common/Layout/Pagination";
import DeleteModal from "components/PopupForms/DeleteDialog/DeleteModal";
import SEO from "components/common/SEO";
import { getFormattedUserName } from "helpers/chats";
import { FORUM } from "endpointsConst";
import { GROUP_ROLES } from "constants/user";
import { showSuccessMessage, showErrorMessage } from "helpers/notifications";
import { ITEMS_PER_PAGE } from "constants/settings";
import { ForumTopicsData, ForumTopic, DeleteModalData } from "types/forum";

import "./forumTopics.scss";

const ForumTopics: React.FC = () => {
    const isAuthenticated = useSelector((state: any) => state.auth.authenticated);
    const groupRoleId = useSelector((state: any) => state.profile.profileData?.group_role_id);
    const isAdmin = groupRoleId === GROUP_ROLES.ADMIN;

    const { topicId } = useParams<{ topicId: string }>();
    const [open, handleOpen] = useState<boolean>(false);
    const [data, setData] = useState<ForumTopicsData>({
        items: [],
        count: 0,
        category_title: "",
    });
    const [loading, setLoading] = useState<boolean>(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const getDateTranslate = (dateString: string): string => {
        const date = new Date(dateString);
        const now = new Date();

        const isToday = date.toDateString() === now.toDateString();
        const isYesterday = new Date(now.setDate(now.getDate() - 1)).toDateString() === date.toDateString();

        const time = date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });

        return isToday
            ? `${t("forums.topics.topic_section.today")} ${time}`
            : isYesterday
            ? `${t("forums.topics.topic_section.yesterday")} ${time}`
            : `${date.toLocaleDateString("ru-RU")} в ${time}`;
    };

    const handleClick = (id: number): void => {
        navigate(`/forums/topic/${topicId}/ticket/${id}`);
    };

    const topicsLoad = useCallback(() => {
        setLoading(true);
        axios
            .get<ForumTopicsData>(FORUM + "/tickets" + location.search, {
                params: {
                    limit: ITEMS_PER_PAGE,
                    forum_category_id: topicId,
                },
            })
            .then((res) => {
                setData(res.data);
            })
            .catch((e) => {
                console.log(e.response);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [location.search, topicId]);

    useEffect(() => {
        topicsLoad();
    }, [location.search, topicsLoad]);

    const onClose = (): void => {
        handleOpen(false);
    };

    const handleDeleteTopic = useCallback(async (ticketId: number, closeCallback: () => void) => {
        try {
            await axios.delete(`${FORUM}/tickets/${ticketId}`);
            showSuccessMessage(t("forums.topic.topic_deleted"));
            topicsLoad();
            closeCallback();
        } catch (error) {
            console.error("Error deleting topic:", error);
            showErrorMessage(t("errors.serverError"));
        }
    }, [topicsLoad, t]);

    const hasPagination = data.count / ITEMS_PER_PAGE > 1;
    const colSpan = isAdmin ? 5 : 4;

    return (
        <>
            <SEO
                title={data.category_title ? `${data.category_title} - Форум` : "Раздел форума"}
                description={`Обсуждения в разделе ${data.category_title || "форума"}. Темы и вопросы от участников сообщества грузоперевозчиков.`}
            />
            <ScrollToTopOnMount />
            <BackButton />
            <PageTitle>{data.category_title || t("forums.heading")}</PageTitle>
            {isAuthenticated && (
                <button
                    className="forums__add-button button--blue"
                    type="button"
                    onClick={() => handleOpen(!open)}
                >
                    {t("forums.topics.add_button")}
                </button>
            )}
            {open && <TopicAddForm id={topicId} onClose={onClose} />}
            <table className="data-table forum-topics" cellSpacing={0}>
                <thead className="data-table__table-header">
                    <tr>
                        <th className="data-table__head" align="left">
                            {t("forums.topics.headings.topic")}
                        </th>
                        <th className="data-table__head" align="center">
                            {t("forums.topics.headings.author")}
                        </th>
                        <th className="data-table__head" align="center">
                            {t("forums.topics.headings.replies")}
                        </th>
                        <th className="data-table__head" align="center">
                            {t("forums.topics.headings.last_message")}
                        </th>
                        {isAdmin && (
                            <th className="data-table__head" align="center">
                                {t("forums.topics.headings.actions")}
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={colSpan}>
                                <Loading />
                            </td>
                        </tr>
                    ) : (
                        <>
                            {data.items?.length === 0 && (
                                <tr>
                                    <td colSpan={colSpan} style={{ padding: "20px" }}>
                                        <NoTopicMessage message={t("forums.topics.empty")} />
                                    </td>
                                </tr>
                            )}
                            {data.items?.map((el: ForumTopic) => {
                                const deleteModalData: DeleteModalData = {
                                    title: t("forums.topic.delete_topic_title"),
                                    id: el.id,
                                    name: el.theme,
                                    type: t("forums.topic.topic_type"),
                                };

                                return (
                                    <tr
                                        className="data-table__table-row data-table__table-column"
                                        onClick={() => handleClick(el.id)}
                                        key={el.id}
                                    >
                                        <td className="data-table__cell">
                                            <span className="forum-topics__headings">
                                                {t("forums.topics.headings.topic")}
                                            </span>
                                            <p className="forums__title">{el.theme || "-"}</p>
                                        </td>
                                        <td className="data-table__cell" align="center">
                                            <span className="forum-topics__headings">
                                                {t("forums.topics.headings.author")}
                                            </span>
                                            <p className="forums__count">
                                                {getFormattedUserName(el.user)}
                                            </p>
                                        </td>
                                        <td className="data-table__cell" align="center">
                                            <span className="forum-topics__headings">
                                                {t("forums.topics.headings.replies")}
                                            </span>
                                            <p className="forums__count">
                                                {el.forum_messages_count || "0"}
                                            </p>
                                        </td>
                                        <td className="data-table__cell" align="center">
                                            <span className="forum-topics__headings">
                                                {t("forums.topics.headings.last_message")}
                                            </span>
                                            <p className="forums__time">
                                                {el.last_created_at
                                                    ? getDateTranslate(el.last_created_at)
                                                    : "-"}
                                            </p>
                                        </td>
                                        {isAdmin && (
                                            <td
                                                className="data-table__cell forum-topics__actions"
                                                align="center"
                                                onClick={(e: MouseEvent) => e.stopPropagation()}
                                            >
                                                <DeleteModal
                                                    data={deleteModalData}
                                                    onDeleteClick={handleDeleteTopic}
                                                />
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </>
                    )}
                </tbody>
            </table>
            {hasPagination && (
                <Pagination
                    itemsOnPage={ITEMS_PER_PAGE}
                    count={data.count}
                    url={`/forums/topic/${topicId}`}
                />
            )}
        </>
    );
};

export default ForumTopics;
